import { useMutation } from '@tanstack/react-query';
import { ValidationError } from 'yup';
import {
  TransferAppointmentDepositPayload,
  finalTransferAppointmentDepositSchema,
} from '../../types/appointments';
import useUser from '../global/useUser';
import { transferAppointmentDeposit } from '../../services/appointments';

interface Params {
  onRefresh?: () => void | Promise<void>;
  onSuccess?: (
    result: Awaited<ReturnType<typeof transferAppointmentDeposit>>,
  ) => void;
  onError?: (error: string) => void;
}

function useTransferAppointmentDeposit({
  onRefresh,
  onSuccess,
  onError,
}: Params) {
  const { user } = useUser();

  return useMutation({
    mutationFn: async (
      payload: Omit<TransferAppointmentDepositPayload, 'businessId'>,
    ) => {
      if (!user?.business?.id) throw new Error('Invalid business');
      const validatedPayload = await finalTransferAppointmentDepositSchema
        .noUnknown()
        .validate(
          { ...payload, businessId: user?.business?.id },
          {
            stripUnknown: true,
          },
        );

      const result = await transferAppointmentDeposit(validatedPayload);

      try {
        await onRefresh?.();
      } catch (error) {
        // Empty
      }

      return result;
    },
    onSuccess,
    onError: error => {
      if (error instanceof ValidationError) {
        onError?.(error.message);
      } else {
        onError?.('Failed to transfer deposit');
      }
    },
  });
}

export default useTransferAppointmentDeposit;
